import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import cn from 'classnames';
import localization from '../../shared/strings';
import Tooltip from '../Tooltip';
import { pixelsConfig } from '../DownloadDialog/config';

const GENERIC_RATIO_ROW_KEYS = [
  { key: 'original', labelKey: 'cropAspectOriginal' },
  { key: '1:1', labelKey: 'cropAspect1_1' },
  { key: '4:3', labelKey: 'cropAspect4_3' },
  { key: '3:4', labelKey: 'cropAspect3_4' },
  { key: '16:9', labelKey: 'cropAspect16_9' },
  { key: '9:16', labelKey: 'cropAspect9_16' },
];

/**
 * Keep typed value within `maxAllowed`. Native `input[type=number]` does not
 * enforce `max` while typing.
 */
function clampDimensionDraft(raw, maxAllowed) {
  if (raw === '') return raw;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return raw;
  if (n > maxAllowed) return String(maxAllowed);
  return raw;
}

/**
 * Build chip descriptors from either a curated `customRatios` list (default
 * social presets like "Instagram Post") or the generic ratio row.
 */
function toChips(customRatios, L) {
  if (!Array.isArray(customRatios) || customRatios.length === 0) {
    return GENERIC_RATIO_ROW_KEYS.map(({ key, labelKey }) => ({
      key,
      label: L[labelKey],
    }));
  }

  return customRatios.map((r) => ({
    key: r.ratio,
    label: r.ratio,
    tooltip: r.tooltip,
    width: r.width,
    height: r.height,
    size: (r.width && r.height) ? `${r.width}\u00d7${r.height}` : null,
    safeArea: r.safeArea,
  }));
}

export default function AssetsCopyCropAspectPanel({
  variant,
  aspectPreset,
  onAspectPresetChange,
  cropPixelWidth,
  cropPixelHeight,
  onCropPixelsCommit,
  sourceNaturalWidth,
  sourceNaturalHeight,
  dimensionsDisabled,
  disabled,
  customRatios,
}) {
  const L = localization.COPY_ASSETS_DIALOG;
  const hasCustomRatios = Array.isArray(customRatios) && customRatios.length > 0;
  const showFreeForm = variant === 'full' && !hasCustomRatios;
  const showDimensions = variant === 'full';

  const chips = toChips(customRatios, L);
  const activeChip = chips.find((c) => c.key === aspectPreset);

  // For curated social presets: if the source image can fit the preset's target
  // resolution (e.g. 1080×1350 for Instagram Post 4:5), display that target.
  // Otherwise display the real max-centered crop rect fed from the preview,
  // since the zipper's resize uses "shrink-only" and won't upscale beyond
  // source pixels. Fields are read-only in this mode — the preview crop is
  // locked to this aspect (no resize handles).
  const presetTargetW = hasCustomRatios ? activeChip?.width : undefined;
  const presetTargetH = hasCustomRatios ? activeChip?.height : undefined;
  const canFitPresetTarget = (
    presetTargetW != null
    && presetTargetH != null
    && typeof cropPixelWidth === 'number'
    && typeof cropPixelHeight === 'number'
    && cropPixelWidth >= presetTargetW
    && cropPixelHeight >= presetTargetH
  );
  const displayW = canFitPresetTarget ? presetTargetW : cropPixelWidth;
  const displayH = canFitPresetTarget ? presetTargetH : cropPixelHeight;
  const dimensionsFieldDisabled = hasCustomRatios || dimensionsDisabled;
  const dimensionsEditable = !hasCustomRatios && !dimensionsDisabled;
  /** Max for width/height fields: never above source image (4:3, 1:1, free, etc.). */
  const pixelWidthInputMax = (
    typeof sourceNaturalWidth === 'number'
      ? Math.min(pixelsConfig.max, sourceNaturalWidth)
      : pixelsConfig.max
  );
  const pixelHeightInputMax = (
    typeof sourceNaturalHeight === 'number'
      ? Math.min(pixelsConfig.max, sourceNaturalHeight)
      : pixelsConfig.max
  );

  const [wDraft, setWDraft] = useState('');
  const [hDraft, setHDraft] = useState('');

  useEffect(() => {
    setWDraft(displayW != null ? String(displayW) : '');
    setHDraft(displayH != null ? String(displayH) : '');
  }, [displayW, displayH]);

  const commitDimensions = useCallback(() => {
    if (!showDimensions || !dimensionsEditable) return;
    let w = parseInt(wDraft, 10);
    let h = parseInt(hDraft, 10);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
      setWDraft(displayW != null ? String(displayW) : '');
      setHDraft(displayH != null ? String(displayH) : '');
      return;
    }
    if (typeof sourceNaturalWidth === 'number' && typeof sourceNaturalHeight === 'number') {
      w = Math.min(w, sourceNaturalWidth);
      h = Math.min(h, sourceNaturalHeight);
    }
    onCropPixelsCommit(w, h);
  }, [
    dimensionsEditable,
    displayH,
    displayW,
    hDraft,
    onCropPixelsCommit,
    showDimensions,
    sourceNaturalHeight,
    sourceNaturalWidth,
    wDraft,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        commitDimensions();
        e.target.blur();
      }
    },
    [commitDimensions],
  );

  const handleWDraftChange = useCallback(
    (e) => {
      setWDraft(clampDimensionDraft(e.target.value, pixelWidthInputMax));
    },
    [pixelWidthInputMax],
  );

  const handleHDraftChange = useCallback(
    (e) => {
      setHDraft(clampDimensionDraft(e.target.value, pixelHeightInputMax));
    },
    [pixelHeightInputMax],
  );

  const renderChip = (chip) => {
    const btn = (
      <button
        key={chip.key}
        type="button"
        className={`assetsCopyDialog__formatButton${aspectPreset === chip.key ? ' is-active' : ''}`}
        onClick={() => onAspectPresetChange(chip.key)}
        disabled={disabled}
      >
        {chip.label}
      </button>
    );
    return (
      <Choose>
        <When condition={chip.tooltip}>
          <Tooltip key={chip.key} content={chip.tooltip} placement="top">{btn}</Tooltip>
        </When>
        <Otherwise>{btn}</Otherwise>
      </Choose>
    );
  };

  const ratioButtons = chips.map(renderChip);

  if (variant === 'bulk') {
    return (
      <div className={cn('assetsCopyDialog__format assetsCopyDialog__cropRatioBulk', disabled && 'assetsCopyDialog__format--disabled')}>
        <div className="assetsCopyDialog__formatLabel">{L.cropRatioLabel}</div>
        <div className="assetsCopyDialog__formatButtons">
          {ratioButtons}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('assetsCopyDialog__cropPanel', disabled && 'assetsCopyDialog__cropPanel--disabled')}>
      <div className="assetsCopyDialog__formatLabel">{L.cropRatioLabel}</div>
      <div className="assetsCopyDialog__formatButtons assetsCopyDialog__formatButtons--wrap">
        {ratioButtons}
        <If condition={showFreeForm}>
          <button
            type="button"
            className={`assetsCopyDialog__formatButton${aspectPreset === 'free' ? ' is-active' : ''}`}
            onClick={() => onAspectPresetChange('free')}
            disabled={disabled}
          >
            {L.cropAspectFreeForm}
          </button>
        </If>
      </div>

      <If condition={showDimensions}>
        <div className="assetsCopyDialog__cropDimsRow">
          <TextField
            className="assetsCopyDialog__cropDimField"
            label={L.cropWidthLabel}
            type="number"
            value={wDraft}
            onChange={handleWDraftChange}
            onBlur={commitDimensions}
            onKeyDown={handleKeyDown}
            disabled={dimensionsFieldDisabled}
            min={pixelsConfig.min}
            max={pixelWidthInputMax}
          />
          <TextField
            className="assetsCopyDialog__cropDimField"
            label={L.cropHeightLabel}
            type="number"
            value={hDraft}
            onChange={handleHDraftChange}
            onBlur={commitDimensions}
            onKeyDown={handleKeyDown}
            disabled={dimensionsFieldDisabled}
            min={pixelsConfig.min}
            max={pixelHeightInputMax}
          />
        </div>
      </If>
    </div>
  );
}

AssetsCopyCropAspectPanel.propTypes = {
  variant: PropTypes.oneOf(['full', 'bulk']),
  aspectPreset: PropTypes.string.isRequired,
  onAspectPresetChange: PropTypes.func.isRequired,
  cropPixelWidth: PropTypes.number,
  cropPixelHeight: PropTypes.number,
  onCropPixelsCommit: PropTypes.func,
  /** Source image size in pixels (caps crop width/height inputs for generic ratios and freeform). */
  sourceNaturalWidth: PropTypes.number,
  sourceNaturalHeight: PropTypes.number,
  dimensionsDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  customRatios: PropTypes.arrayOf(PropTypes.shape({
    ratio: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    tooltip: PropTypes.string,
    isDefault: PropTypes.bool,
    safeArea: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  })),
};

AssetsCopyCropAspectPanel.defaultProps = {
  variant: 'full',
  cropPixelWidth: undefined,
  cropPixelHeight: undefined,
  onCropPixelsCommit: () => {},
  sourceNaturalWidth: undefined,
  sourceNaturalHeight: undefined,
  dimensionsDisabled: false,
  disabled: false,
  customRatios: null,
};
